import { supabase } from '@/integrations/supabase/client';
import { encryptField, decryptField } from './encryption-service';

export interface BudgetHistoryEntry {
  id: string;
  budget_id: string;
  user_id: string;
  profile_id?: string;
  period: string;
  budgeted: number;
  spent: number;
  start_date: string;
  end_date: string;
  created_at?: string;
  updated_at?: string;
}

export const saveBudgetHistoryToSupabase = async (
  historyEntry: Omit<BudgetHistoryEntry, 'id' | 'created_at' | 'updated_at'>
): Promise<{ success: boolean; error?: string; data?: BudgetHistoryEntry }> => {
  try {
    // Criptografar campos sensíveis
    const budgetedEncrypted = await encryptField(historyEntry.budgeted.toString());
    const spentEncrypted = await encryptField(historyEntry.spent.toString());

    const encryptedData = {
      budget_id: historyEntry.budget_id,
      user_id: historyEntry.user_id,
      profile_id: historyEntry.profile_id || null,
      period: historyEntry.period,
      budgeted: historyEntry.budgeted, // Manter valor original para queries
      spent: historyEntry.spent, // Manter valor original para queries
      budgeted_encrypted: budgetedEncrypted.encrypted,
      budgeted_nonce: budgetedEncrypted.nonce,
      spent_encrypted: spentEncrypted.encrypted,
      spent_nonce: spentEncrypted.nonce,
      start_date: historyEntry.start_date,
      end_date: historyEntry.end_date,
    };

    const { data, error } = await supabase
      .from('budget_history')
      .insert(encryptedData)
      .select()
      .single();

    if (error) {
      console.error('Error saving budget history:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: data as BudgetHistoryEntry };
  } catch (error) {
    console.error('Error in saveBudgetHistoryToSupabase:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

export const loadBudgetHistoryFromSupabase = async (
  budgetId: string
): Promise<BudgetHistoryEntry[]> => {
  try {
    const { data, error } = await supabase
      .from('budget_history')
      .select('*')
      .eq('budget_id', budgetId)
      .order('start_date', { ascending: false });

    if (error) {
      console.error('Error loading budget history:', error);
      return [];
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Descriptografar cada entrada
    const decryptedHistory = await Promise.all(
      data.map(async (entry: any) => {
        try {
          let budgeted = entry.budgeted;
          let spent = entry.spent;

          // Descriptografar se os campos criptografados existirem
          if (entry.budgeted_encrypted && entry.budgeted_nonce) {
            const decryptedBudgeted = await decryptField({
              encrypted: entry.budgeted_encrypted,
              nonce: entry.budgeted_nonce
            });
            budgeted = parseFloat(decryptedBudgeted);
          }

          if (entry.spent_encrypted && entry.spent_nonce) {
            const decryptedSpent = await decryptField({
              encrypted: entry.spent_encrypted,
              nonce: entry.spent_nonce
            });
            spent = parseFloat(decryptedSpent);
          }

          return {
            id: entry.id,
            budget_id: entry.budget_id,
            user_id: entry.user_id,
            profile_id: entry.profile_id,
            period: entry.period,
            budgeted,
            spent,
            start_date: entry.start_date,
            end_date: entry.end_date,
            created_at: entry.created_at,
            updated_at: entry.updated_at,
          } as BudgetHistoryEntry;
        } catch (decryptError) {
          console.error('Error decrypting history entry:', decryptError);
          // Retornar valores não criptografados como fallback
          return {
            id: entry.id,
            budget_id: entry.budget_id,
            user_id: entry.user_id,
            profile_id: entry.profile_id,
            period: entry.period,
            budgeted: entry.budgeted || 0,
            spent: entry.spent || 0,
            start_date: entry.start_date,
            end_date: entry.end_date,
            created_at: entry.created_at,
            updated_at: entry.updated_at,
          } as BudgetHistoryEntry;
        }
      })
    );

    return decryptedHistory;
  } catch (error) {
    console.error('Error in loadBudgetHistoryFromSupabase:', error);
    return [];
  }
};

export const createHistorySnapshot = async (
  budgetId: string,
  userId: string,
  profileId: string | undefined,
  period: string,
  budgeted: number,
  spent: number,
  startDate: string,
  endDate: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Verificar se já existe histórico para este período
    const { data: existingHistory, error: checkError } = await supabase
      .from('budget_history')
      .select('id')
      .eq('budget_id', budgetId)
      .eq('start_date', startDate)
      .eq('end_date', endDate)
      .maybeSingle();

    if (checkError && checkError.code !== 'PGRST116') {
      console.error('Error checking existing history:', checkError);
      return { success: false, error: checkError.message };
    }

    // Se já existe, fazer UPDATE (upsert) com os novos valores
    if (existingHistory) {
      console.log(`[HISTORY] Snapshot exists for budget ${budgetId} period ${startDate} to ${endDate}, updating spent=${spent} and budgeted=${budgeted}`);
      
      // Criptografar os novos valores
      const budgetedEncrypted = await encryptField(budgeted.toString());
      const spentEncrypted = await encryptField(spent.toString());

      const { error: updateError } = await supabase
        .from('budget_history')
        .update({
          budgeted,
          spent,
          budgeted_encrypted: budgetedEncrypted.encrypted,
          budgeted_nonce: budgetedEncrypted.nonce,
          spent_encrypted: spentEncrypted.encrypted,
          spent_nonce: spentEncrypted.nonce,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existingHistory.id);

      if (updateError) {
        console.error('Error updating budget history:', updateError);
        return { success: false, error: updateError.message };
      }

      console.log(`[HISTORY] Successfully updated history snapshot`);
      return { success: true };
    }

    // Se não existe, criar novo registro
    return saveBudgetHistoryToSupabase({
      budget_id: budgetId,
      user_id: userId,
      profile_id: profileId,
      period,
      budgeted,
      spent,
      start_date: startDate,
      end_date: endDate,
    });
  } catch (error) {
    console.error('Error in createHistorySnapshot:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};
